import { useEffect } from "react";
import { AuthStore, dispatch, useAuthSelector } from "@nf/common";
import {
  LOGIN_TOKEN_URL_PARAM,
  REDIRECT_URL_PARAM,
  ROUTES,
} from "@nf/constants";
import { useCountry } from "./useCountry";
import { useLanguage } from "./useLanguage";
import { useRouter } from "./useRouter";

interface ISignInViaTokenParamOptions {
  successUrl: string;
  loginRedirectUrl: string;
  enabled?: boolean;
}

/**
 * Signs the user in with the `login_token` query param.
 */
export const useSignInViaTokenParam = ({
  successUrl,
  loginRedirectUrl,
  enabled = true,
}: ISignInViaTokenParamOptions) => {
  const { isCompleted, error } = useAuthSelector();

  const country = useCountry();
  const language = useLanguage();

  const router = useRouter();
  const loginToken = enabled ? router.query[LOGIN_TOKEN_URL_PARAM] : undefined;

  useEffect(() => {
    if (loginToken && country && language && !isCompleted) {
      dispatch(
        AuthStore.Actions.signInViaToken({
          LoginToken: loginToken,
          Country: country,
          Language: language,
        })
      );
    }
  }, [loginToken, country, language, isCompleted]);

  useEffect(() => {
    if (!loginToken || !isCompleted) {
      return;
    }

    if (error) {
      router.replace(
        {
          pathname: ROUTES.LOGIN,
          query: { [REDIRECT_URL_PARAM]: loginRedirectUrl },
        },
        undefined,
        { withLoadingSplash: true }
      );
    } else {
      // After logging in on Settings user stays on the same page so shallow is needed
      const isTheSamePage = router.asPath.split("?")[0].endsWith(successUrl);
      router.replace(successUrl, undefined, { shallow: isTheSamePage });
    }
  }, [loginToken, isCompleted, error, successUrl, loginRedirectUrl]);

  return !!loginToken;
};
